import React, { useState, useEffect, useRef, useContext } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { fs } from '../firebase';
import '../css/AddressSheet.css';
import LocationPicker from '../pages/DeliveryInterface/LocationPicker';
import { LocalizationContext } from '../localization/LocalizationContext';

const AddressSheet = ({ onClose, onSelectAddress, currentAddress }) => {
    const { translations } = useContext(LocalizationContext);
    const as = translations?.addressSheet || {};
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [gpsLoading, setGpsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const searchTimeout = useRef(null);

    const getUser = () => getAuth().currentUser;

    useEffect(() => {
        const user = getUser();
        if (!user) return;
        getDoc(doc(fs, 'users', user.uid)).then(snap => {
            if (snap.exists()) {
                setSavedAddresses(snap.data().savedAddresses || []);
            }
        }).catch(() => {});
    }, []);

    const persistAddress = () => {}; // address saved only after successful order

    const handleUseGps = () => {
        if (!navigator.geolocation) return;
        setGpsLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude: lat, longitude: lng } = pos.coords;
                try {
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
                    );
                    const data = await res.json();
                    const addr = data.address || {};
                    const house = addr.house_number;
                    const road = addr.road;
                    const city = addr.city || addr.town || addr.state;
                    const text = road && house ? `${house}, ${road}` : road && city ? `${road}, ${city}` : city || data.display_name;
                    persistAddress(text, lat, lng);
                    onSelectAddress({ address: text, lat, lng });
                    onClose();
                } catch {
                    setGpsLoading(false);
                } finally {
                    setGpsLoading(false);
                }
            },
            () => setGpsLoading(false),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleSearchChange = (e) => {
        const q = e.target.value;
        setSearchQuery(q);
        clearTimeout(searchTimeout.current);
        if (q.trim().length < 3) { setSearchResults([]); return; }
        setSearching(true);
        searchTimeout.current = setTimeout(async () => {
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5&countrycodes=ge`
                );
                const data = await res.json();
                setSearchResults(data);
            } catch {
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 500);
    };

    const handleSearchSelect = (item) => {
        const text = item.display_name;
        const lat = parseFloat(item.lat);
        const lng = parseFloat(item.lon);
        persistAddress(text, lat, lng);
        onSelectAddress({ address: text, lat, lng });
        onClose();
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        const updated = savedAddresses.filter(a => a.id !== id);
        setSavedAddresses(updated);
        const user = getUser();
        if (user) {
            await setDoc(doc(fs, 'users', user.uid), { savedAddresses: updated }, { merge: true }).catch(() => {});
        }
    };

    const handleSelect = (item) => {
        onSelectAddress({ address: item.address, lat: item.lat, lng: item.lng });
        onClose();
    };

    const handleMapConfirm = (loc) => {
        persistAddress(loc.address, loc.lat, loc.lng);
        onSelectAddress(loc);
        onClose();
    };

    if (showMap) {
        return (
            <div className="as-overlay">
                <div className="as-modal as-modal-fullmap">
                    <LocationPicker
                        onConfirm={handleMapConfirm}
                        onBack={() => setShowMap(false)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="as-overlay" onClick={onClose}>
            <div className="as-modal" onClick={e => e.stopPropagation()}>

                <div className="as-header">
                    <span className="as-title">{as.title || 'Delivery Address'}</span>
                    <button className="as-close" onClick={onClose}>✕</button>
                </div>

                {/* Manual search input */}
                <div className="as-search-wrap">
                    <svg className="as-search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="#9ca3af" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"/>
                        <path d="M21 21l-6 -6"/>
                    </svg>
                    <input
                        className="as-search-input"
                        type="text"
                        placeholder={as.placeholder || 'Enter address manually...'}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        autoFocus
                    />
                    {searching && <div className="as-search-spinner" />}
                </div>

                {/* Search results */}
                {searchResults.length > 0 && (
                    <div className="as-results">
                        {searchResults.map((item, i) => (
                            <div key={i} className="as-result-item" onClick={() => handleSearchSelect(item)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" stroke="#9ca3af" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                    <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/>
                                    <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"/>
                                </svg>
                                <span>{item.display_name}</span>
                            </div>
                        ))}
                    </div>
                )}

                <div className="as-divider" />

                {/* GPS */}
                <button className="as-option-btn" onClick={handleUseGps} disabled={gpsLoading}>
                    <div className="as-option-icon as-option-icon--red">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/>
                            <path d="M12 3l0 3"/><path d="M12 18l0 3"/>
                            <path d="M3 12l3 0"/><path d="M18 12l3 0"/>
                        </svg>
                    </div>
                    <div className="as-option-text">
                        <span className="as-option-label">{gpsLoading ? (as.loading || 'Loading...') : (as.myLocation || 'My Location')}</span>
                        <span className="as-option-sub">{as.gpsSub || 'Automatic GPS detection'}</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" stroke="#d1d5db" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M9 6l6 6l-6 6"/>
                    </svg>
                </button>

                {/* Map picker */}
                <button className="as-option-btn" onClick={() => setShowMap(true)} style={{ marginBottom: '20px' }}>
                    <div className="as-option-icon as-option-icon--blue">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M3 7l6 -3l6 3l6 -3l0 13l-6 3l-6 -3l-6 3l0 -13"/>
                            <path d="M9 4l0 13"/><path d="M15 7l0 13"/>
                        </svg>
                    </div>
                    <div className="as-option-text">
                        <span className="as-option-label">{as.mapLabel || 'Mark on map'}</span>
                        <span className="as-option-sub">{as.mapSub || 'Move the pin to desired location'}</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" stroke="#d1d5db" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M9 6l6 6l-6 6"/>
                    </svg>
                </button>

                {/* Saved addresses — only for logged-in users */}
                {getUser() && savedAddresses.length > 0 && (
                    <div className="as-section">
                        <div className="as-section-title">{as.savedAddresses || 'Saved Addresses'}</div>
                        {savedAddresses.map(item => {
                            const isActive = currentAddress === item.address;
                            return (
                                <div
                                    key={item.id}
                                    className={`as-address-item ${isActive ? 'active' : ''}`}
                                    onClick={() => !isActive && handleSelect(item)}
                                >
                                    <div className="as-address-icon">
                                        {isActive ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2.5" stroke="#ef4444" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                <path d="M5 12l5 5l10 -10"/>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/>
                                                <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"/>
                                            </svg>
                                        )}
                                    </div>
                                    <span className="as-address-text">{item.address}</span>
                                    {!isActive && (
                                        <button className="as-delete-btn" onClick={(e) => handleDelete(item.id, e)}>✕</button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddressSheet;
